import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "@/api/api.js";
import $ from "jquery";
import "datatables.net";
import languageES from "datatables.net-plugins/i18n/es-MX";
import { Card, Col, Button, Spinner, Alert } from "react-bootstrap";
import { FaDownload } from "react-icons/fa";
import { useCompany } from "@/context/CompanyContext.jsx";
import { useTranslation } from "react-i18next";

const CompanyContactsShow = () => {
  const { selectedCompany } = useCompany();
  const { contactId } = useParams();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tableRef = useRef(null);
  const dataTable = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/contacts/${contactId}`)
      .then(({ data }) => setContact(data))
      .catch((err) => {
        console.error("Error al cargar contacto:", err);
        setError(t("ui.clientLoadError"));
      })
      .finally(() => setLoading(false));
  }, [contactId, t]);

  useEffect(() => {
    if (!tableRef.current || !contactId || !selectedCompany) return;

    if ($.fn.DataTable.isDataTable(tableRef.current)) {
      $(tableRef.current).DataTable().destroy();
      $(tableRef.current).empty();
    }

    dataTable.current = $(tableRef.current).DataTable({
      language: languageES,
      processing: true,
      serverSide: true,
      ajax: (dtParams, callback) => {
        api
          .get(`/billings/${contactId}/${selectedCompany}/bills`)
          .then(({ data }) => {
            callback({
              draw: dtParams.draw,
              recordsTotal: data["hydra:totalItems"],
              recordsFiltered: data["hydra:totalItems"],
              data: data["hydra:member"],
            });
          })
          .catch(() =>
            callback({
              draw: dtParams.draw,
              recordsTotal: 0,
              recordsFiltered: 0,
              data: [],
            }),
          );
      },
      columns: [{ title: "ID Factura", data: "id" }],
    });

    return () => {
      if ($.fn.DataTable.isDataTable(tableRef.current)) {
        $(tableRef.current).DataTable().destroy();
      }
    };
  }, [contactId, selectedCompany, loading]);

  const handleDownloadCertificate = async () => {
    if (!contact) return;

    try {
      const response = await api.get(
        `/contacts/${contact.id}/downloadCertificateTax`,
        {
          responseType: "blob",
        },
      );

      let filename = "certificado.pdf";

      const disposition = response.headers["content-disposition"];
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      } else if (contact.certificateTaxOriginalName) {
        // Por si tú guardas el nombre original en el contacto
        filename = contact.certificateTaxOriginalName;
      }

      // Crear un blob y URL temporal
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);

      // Crear un enlace temporal y simular click
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Limpiar
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error descargando certificado:", error);
      alert("No se pudo descargar el certificado. Intenta más tarde.");
    }
  };

  if (loading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Cargando...</span>
      </Spinner>
    );
  }

  if (error || !contact) {
    return (
      <Alert variant="danger">
        {error || t("ui.clientLoadError")}
      </Alert>
    );
  }

  return (
    <>
      <Col xs={12}>
        <Card className="mb-4">
          <Card.Header>{t("ui.clientDetail")}</Card.Header>
          <Card.Body>
            <p>
              <strong>Razón Social:</strong> {contact.businessName}
            </p>
            <p>
              <strong>RFC:</strong> {contact.taxIdentification}
            </p>
            <p>
              <strong>Teléfono:</strong> {contact.phone}
            </p>
            <p>
              <strong>Dirección:</strong>{" "}
              {`${contact.street} ${contact.exterior}${contact.interior ? " Int. " + contact.interior : ""}, ${contact.suburb}, ${contact.town}, ${contact.state}, CP ${contact.postalCode}`}
            </p>
            <p>
              <strong>Tipo:</strong> {contact.type}
            </p>
            <p>
              <strong>Email:</strong> {contact.emailContact}
            </p>
            <p>
              <strong>Creado:</strong> {contact.createdAtAgo}
            </p>
            {contact.certificateTaxPath && (
              <Button
                variant="outline-primary"
                onClick={handleDownloadCertificate}
                target="_blank"
                className="mt-2"
              >
                <FaDownload className="me-2" />
                Descargar Certificado
              </Button>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>Facturas</Card.Header>
          <Card.Body>
            <table
              ref={tableRef}
              className="display"
              style={{ width: "100%" }}
            ></table>
          </Card.Body>
        </Card>
      </Col>
    </>
  );
};

export default CompanyContactsShow;
